import { create } from 'zustand'
import {
  setOrgViewState,
  clearOrgViewState,
  type OrgViewSide,
} from '../lib/orgViewState'
import { setActAsOrg } from '../lib/api'

// Platform Admin Organization Context Access (D-5 Ring 3 governance bypass).
//
// NOT impersonation: the platform admin keeps their A1 identity but opens a
// selected Seller/Vendor organization's dashboard with org-admin-level
// visibility. V1 = read-only "transparent glass" (mutations blocked centrally
// in MSW). The MSW mirror (orgViewState) is kept in sync for the read-only
// guard, which runs outside React.

export type ViewingOrg = {
  side: OrgViewSide
  orgId: string
  orgName: string
  orgType: string
}

type OrgContextStore = {
  mode: 'self' | 'platform_viewing'
  viewing: ViewingOrg | null
  enterOrg: (org: ViewingOrg, enteredByUserId: string, enteredAt: string) => void
  exitOrg: () => void
}

// Persist the view-as context so an admin stays "in" the entered org across a
// page reload (the X-Act-As-Org header + read-only guard must survive refresh,
// else the seller surface 403s the admin again). Hydrated at startup.
const STORAGE_KEY = 'pexify-org-view'

function persistViewing(org: ViewingOrg | null): void {
  if (typeof window === 'undefined') return
  try {
    if (org) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(org))
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Best-effort; private-mode / quota failures are non-fatal.
  }
}

function readPersisted(): ViewingOrg | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const o = JSON.parse(raw) as ViewingOrg
    if (o && o.orgId && (o.side === 'seller' || o.side === 'supply')) return o
  } catch {
    // ignore malformed
  }
  return null
}

const _initial = readPersisted()

export const useOrgContextStore = create<OrgContextStore>((set) => ({
  mode: _initial ? 'platform_viewing' : 'self',
  viewing: _initial,
  enterOrg: (org, enteredByUserId, enteredAt) => {
    setOrgViewState({
      mode: 'platform_viewing',
      side: org.side,
      orgId: org.orgId,
      orgName: org.orgName,
      orgType: org.orgType,
      enteredByUserId,
      enteredAt,
    })
    setActAsOrg(org.orgId)
    persistViewing(org)
    set({ mode: 'platform_viewing', viewing: org })
  },
  exitOrg: () => {
    clearOrgViewState()
    setActAsOrg(null)
    persistViewing(null)
    set({ mode: 'self', viewing: null })
  },
}))

// Re-establish the act-as header + MSW read-only mirror from the persisted
// context at startup (the zustand store itself is not persisted). Call once
// before the app renders.
export function hydrateOrgContext(): void {
  const org = readPersisted()
  if (!org) return
  setActAsOrg(org.orgId)
  setOrgViewState({
    mode: 'platform_viewing',
    side: org.side,
    orgId: org.orgId,
    orgName: org.orgName,
    orgType: org.orgType,
    enteredByUserId: null,
    enteredAt: null,
  })
}

/** True while a platform admin is viewing another org (read-only). Money and
 *  other write buttons render disabled; the backend refuses the write anyway. */
export function useIsPlatformViewing(): boolean {
  return useOrgContextStore((s) => s.mode === 'platform_viewing')
}
